import { fileHref } from '../lib/fileAccess'
import { useEffect, useState } from 'react'
import { useLinkedDocs, DocChips } from '../components/SopLinks'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'
import { useAuth } from '../hooks/useAuth'

// web titles sometimes arrive with HTML entities (St Thomas&#39; Hospital)
// names that still carry HTML (an '<a class=...' fragment from an old watcher run) are shown as plain text
function plainText(t) {
  return decodeHtml(String(t || '').replace(/<[^>]*>?/g, ' ')).replace(/\s+/g, ' ').trim()
}
function decodeHtml(t) {
  return String(t || '').replace(/&#(\d+);/g, (_, n) => String.fromCharCode(+n)).replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
}
// **bold** and ## headings in AI answers
function mdBold(t) {
  return String(t || '').replace(/^#{1,4}\s+(.*)$/gm, '**$1**').split(/(\*\*[^*\n]+\*\*)/g)
    .map((seg, i) => /^\*\*[^*\n]+\*\*$/.test(seg) ? <strong key={i}>{seg.slice(2, -2)}</strong> : seg)
}

// a notice taken out of use: retired (by us, with a reason) or superseded (a newer revision exists)
export const isOld = r => r.status === 'retired' || r.status === 'superseded'

const CAT_ORDER = ['Safety', 'Pollution', 'Labour', 'Security', 'Construction', 'Other']

function prettyRegName(p) {
  let raw = String(p.full_name || p.code || '').trim()
  if (!raw) return 'Regulatory update'
  // strip filename artifacts: date stamps, version tags, trailing lone 0
  raw = raw.replace(/\s+\d{1,2}[A-Z]{3}\d{4}\b/gi, '')
           .replace(/\s+V\d+(\s+Final)?$/i, '')
           .replace(/\s+0$/, '').trim()
  const year = (raw.match(/(?:19|20)\d{2}/) || [])[0] || ''
  const tokens = raw.replace(/[-\u2013]/g, ' ').split(/\s+/).filter(Boolean)
  let num = '', grabbed = false
  const descTokens = []
  for (const tk of tokens) {
    if (tk === year) continue
    if (!grabbed && /^\d{1,2}$/.test(tk)) { num = tk; grabbed = true; continue }
    if (/^(CISN|SN|CYC)$/i.test(tk)) continue
    descTokens.push(tk)
  }
  const desc = descTokens.join(' ').trim()
  const ref = (num && year) ? 'CISN ' + String(num).padStart(2, '0') + '/' + year : ''
  if (ref && desc) return ref + ' \u2014 ' + desc
  if (ref) return ref
  return raw
}

export default function Regulations() {
  const { config } = useAuth()
  const isAdmin = !!config?.is_admin
  const [rows, setRows] = useState([])
  const sopLinks = useLinkedDocs('regulation')   // SOPs / manuals that cite a regulation (sql/sop-layer.sql)
  const [old, setOld] = useState([])          // retired / superseded: hidden unless 'Show retired'
  const [showOld, setShowOld] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [q, setQ] = useState('')
  const [pending, setPending] = useState([])
  const [askQ, setAskQ] = useState('')
  const [chat, setChat] = useState([])        // [{role:'user'|'assistant', text, sources?}]
  const [askLoading, setAskLoading] = useState(false)
  const [askError, setAskError] = useState('')

  useEffect(() => { load() }, [config?.id])

  async function load() {
    setLoading(true); setError('')
    const { data, error } = await supabase
      .from('regulations')
      .select('*')
      .or('active.eq.true,status.in.(retired,superseded)')
      .order('sort_order', { ascending: true })
    if (error) setError(error.message)
    else { const all = data || []; setRows(all.filter(r => !isOld(r))); setOld(all.filter(isOld)) }

    const { data: pend } = await supabase
      .from('regulations')
      .select('*')
      .eq('status', 'pending')
      .order('detected_at', { ascending: false })
    let reviewed = new Set()
    if (config?.id) {
      const { data: rv } = await supabase.from('regulation_reviews').select('regulation_id').eq('client_id', config.id)
      reviewed = new Set((rv || []).map(r => String(r.regulation_id)))
    }
    setPending((pend || []).filter(p => isAdmin || !reviewed.has(String(p.id))))

    setLoading(false)
  }

  async function approve(item) {
    const id = typeof item === 'object' ? item.id : item
    // 1. publish to the library
    await supabase.from('regulations').update({ status: 'published', active: true }).eq('id', id)
    // 2. push the document into the AI vector store so the chat knows about it
    const url = typeof item === 'object' ? item.doc_url : null
    const code = typeof item === 'object' ? (item.code || 'regulation') : 'regulation'
    if (url) {
      const isPdf = url.toLowerCase().endsWith('.pdf')
      const isGovUk = url.includes('gov.uk/government/publications/')
      const action = isGovUk ? 'upload_mca_from_page' : 'upload_from_url'
      try {
        await fetch('/api/workspace', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action,
            url,
            file_name: code,
            vector_store_id: 'vs_6a3ca056560481919f8c4ddccb0aba3f',
          }),
        })
      } catch (e) { /* library publish already succeeded; AI upload can be retried */ }
    }
    load()
  }

  // confirm-only: the file is also removed from the AI's library (vector store), which an Undo cannot put back
  async function removeDoc(r) {
    if (!window.confirm('Delete "' + r.code + '" from the library AND the AI? This cannot be undone.')) return
    // 1. remove from AI vector store (by filename match)
    if (r.doc_url) {
      const fname = decodeURIComponent(r.doc_url.split('/').pop() || '')
      try {
        await fetch('/api/workspace', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'delete_from_vector_store', file_name: fname, vector_store_id: 'vs_6a3ca056560481919f8c4ddccb0aba3f' }),
        })
      } catch (e) { /* continue to remove from library even if AI delete hiccups */ }
    }
    // 2. remove from the library
    await supabase.from('regulations').delete().eq('id', r.id)
    load()
  }

  async function review(item, decision) {
    const { error } = await supabase.from('regulation_reviews').upsert({
      client_id: config.id, regulation_id: String(item.id), decision,
      reviewed_by: config?.crewName || 'Captain',
    }, { onConflict: 'client_id,regulation_id' })
    if (error) { alert('Could not save the review: ' + error.message); return }
    load()
  }

  async function dismiss(id) {
    if (!window.confirm('Dismiss this detected item? It will be removed. You can undo it for 10 seconds.')) return
    try { await deleteWithUndo(supabase, { table: 'regulations', id, label: 'detected item', onUndone: () => load() }) } catch (e) { alert('Could not delete: ' + (e.message || e)) }
    load()
  }

  async function askRegulations() {
    const question = askQ.trim()
    if (!question || askLoading) return
    setAskError('')
    setChat(c => [...c, { role: 'user', text: question }])
    setAskQ('')
    setAskLoading(true)
    try {
      const r = await fetch('/api/workspace', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'ask', prompt: question, history: chat.slice(-6).map(m => ({ role: m.role, text: m.text })) }),
      })
      const d = await r.json()
      if (d.error) { setAskError(d.error) }
      else {
        setChat(c => [...c, { role: 'assistant', text: d.answer || '', sources: d.sources || [] }])
      }
    } catch (e) {
      setAskError('Could not reach the assistant. Please try again.')
    } finally {
      setAskLoading(false)
    }
  }

  function resetChat() {
    setChat([]); setAskError(''); setAskQ('')
  }

  const ql = q.trim().toLowerCase()
  const shown = showOld ? rows.concat(old) : rows
  const filtered = !ql ? shown : shown.filter(r =>
    [r.code, r.full_name, r.issuing_body, r.summary, r.applies_to, r.category]
      .filter(Boolean).some(v => v.toLowerCase().includes(ql))
  )

  // group by category
  const groups = {}
  filtered.forEach(r => {
    const c = r.category || 'Other'
    if (!groups[c]) groups[c] = []
    groups[c].push(r)
  })
  const cats = Object.keys(groups).sort((a, b) => {
    const ia = CAT_ORDER.indexOf(a), ib = CAT_ORDER.indexOf(b)
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
  })

  function normKey(str) {
    return (str || '').toLowerCase().replace(/\.pdf$/i, '').replace(/[^a-z0-9]/g, '')
  }
  function findSource(filename) {
    if (!filename) return null
    const fnNorm = normKey(decodeURIComponent(filename))
    let match = rows.find(r => {
      if (!r.doc_url) return false
      const urlFile = decodeURIComponent(r.doc_url.split('/').pop() || '')
      return normKey(urlFile) === fnNorm
    })
    if (match) return match
    match = rows.find(r => {
      const codeNorm = normKey(r.code)
      return codeNorm.length >= 4 && fnNorm.includes(codeNorm)
    })
    return match || null
  }
  function srcLabel(filename) {
    const match = findSource(filename)
    if (match) return match.code + (match.issuing_body ? ' · ' + match.issuing_body : '')
    return (filename || '').replace(/\.pdf$/i, '').replace(/[-_]/g, ' ')
  }

  function srcUrl(filename) {
    const match = findSource(filename)
    return match ? match.doc_url : null
  }

  return (
    <div className="fade-up">
      <div style={{ background: 'linear-gradient(135deg, rgba(201,147,58,.10), rgba(201,147,58,.03))', border: '0.5px solid var(--gold2)', borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--gold2)', letterSpacing: '.03em' }}>Ask the Regulations</span>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>AI answers from the regulation library, with sources</span>
          <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4, fontStyle: 'italic', opacity: .8 }}>AI-assisted guidance — always verify against the linked source document. Not a substitute for official advice.</div>
          {chat.length > 0 && (
            <button onClick={resetChat} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--muted)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>Clear</button>
          )}
        </div>

        {chat.length > 0 && (
          <div style={{ margin: '12px 0', maxHeight: 420, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingRight: 4 }}>
            {chat.map((m, i) => (
              m.role === 'user' ? (
                <div key={i} style={{ alignSelf: 'flex-end', maxWidth: '85%', padding: '8px 13px', borderRadius: '10px 10px 2px 10px', background: 'rgba(201,147,58,.16)', border: '0.5px solid var(--gold2)', fontSize: 13.5, color: 'var(--text)' }}>
                  {m.text}
                </div>
              ) : (
                <div key={i} style={{ alignSelf: 'flex-start', maxWidth: '92%', padding: '12px 15px', borderRadius: '10px 10px 10px 2px', background: 'var(--hull)', border: '0.5px solid var(--border)' }}>
                  <div style={{ fontSize: 13.5, lineHeight: 1.6, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{mdBold(m.text)}</div>
                  {m.sources && m.sources.length > 0 && (
                    <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                      <span style={{ fontSize: 10.5, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Sources:</span>
                      {m.sources.map((src, j) => {
                        const u = srcUrl(src)
                        const chipStyle = { fontSize: 11, padding: '3px 9px', borderRadius: 6, background: 'rgba(201,147,58,.12)', border: '0.5px solid var(--gold2)', color: 'var(--gold2)', textDecoration: 'none', cursor: u ? 'pointer' : 'default' }
                        return u
                          ? <a key={j} href={fileHref(u)} target="_blank" rel="noopener noreferrer" style={chipStyle} title="Open source document">{srcLabel(src)} ↗</a>
                          : <span key={j} style={chipStyle}>{srcLabel(src)}</span>
                      })}
                    </div>
                  )}
                </div>
              )
            ))}
            {askLoading && (
              <div style={{ alignSelf: 'flex-start', fontSize: 13, color: 'var(--muted)', padding: '4px 2px' }}>Searching the regulations library…</div>
            )}
          </div>
        )}

        {askError && <div style={{ margin: '10px 0', fontSize: 13, color: '#b4313c' }}>{askError}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: chat.length > 0 ? 4 : 10 }}>
          <input
            value={askQ}
            onChange={e => setAskQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') askRegulations() }}
            placeholder={chat.length > 0 ? 'Ask a follow-up…' : 'e.g. What are the lifejacket requirements for a 30m commercial yacht?'}
            style={{ flex: 1, padding: '10px 14px', borderRadius: 9, border: '0.5px solid var(--border)', background: 'var(--ink2)', color: 'var(--text)', fontSize: 14, fontFamily: 'inherit' }}
          />
          <button
            onClick={askRegulations}
            disabled={askLoading || !askQ.trim()}
            style={{ padding: '10px 20px', borderRadius: 9, border: 'none', background: 'var(--gold2)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: askLoading || !askQ.trim() ? 'default' : 'pointer', opacity: askLoading || !askQ.trim() ? 0.55 : 1, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
            {askLoading ? 'Thinking…' : 'Ask'}
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search codes, bodies, topics (e.g. MLC, pollution, lifejackets)..."
          className="search-inp"
          style={{ width: '100%', maxWidth: 480 }}
        />
        {old.length > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={showOld} onChange={e => setShowOld(e.target.checked)} />
            Show retired ({old.length})
          </label>
        )}
      </div>

      {pending.length > 0 && (
        <div style={st.pendWrap}>
          <div style={st.pendHd}>
            <span>Pending Review</span>
            <span style={st.pendCount}>{pending.length} new {pending.length === 1 ? 'item' : 'items'} detected</span>
          </div>
          {pending.map(p => (
            <div key={p.id} style={st.pendRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={st.pendTitle}>{plainText(prettyRegName(p))}</div>
                <div style={st.pendMeta}>
                  {p.issuing_body || 'Regulatory body'}
                  {p.detected_at ? ' \u00b7 detected ' + new Date(p.detected_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                {p.doc_url && <a href={p.doc_url} target="_blank" rel="noopener noreferrer" style={st.pendView}>View PDF</a>}
                {isAdmin ? <>
                  <button onClick={() => approve(p)} style={st.pendApprove}>Approve</button>
                  <button onClick={() => dismiss(p.id)} style={st.pendDismiss}>Dismiss</button>
                </> : <>
                  <button onClick={() => review(p, 'acknowledged')} style={st.pendApprove}>Acknowledge</button>
                  <button onClick={() => review(p, 'not_applicable')} style={st.pendDismiss}>Not applicable</button>
                </>}
              </div>
            </div>
          ))}
        </div>
      )}

      {loading && <div style={st.muted}>Loading regulations...</div>}
      {error && <div style={{ color: '#b4313c', fontSize: 13 }}>{error}</div>}
      {!loading && !error && filtered.length === 0 && (
        <div style={st.empty}>No regulations found{ql ? ' for "' + q + '"' : ' yet'}.</div>
      )}

      {cats.map(cat => (
        <div key={cat} style={{ marginBottom: 24 }}>
          <div style={st.catHead}>{cat}</div>
          {groups[cat].map(r => (
            <div key={r.id} style={isOld(r) ? { ...st.card, opacity: .6 } : st.card}>
              <div style={st.cardTop}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <span style={st.code}>{plainText(r.code)}</span>
                    {r.edition && <span style={st.edition}>{r.edition}</span>}
                    {isOld(r) && <span style={st.oldTag}>{r.status === 'superseded' ? 'Superseded' : 'Retired'}</span>}
                    <DocChips docs={sopLinks[String(r.id)]} />
                  </div>
                  {r.full_name && <div style={st.fullName}>{plainText(r.full_name)}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {r.doc_url && <a href={r.doc_url} target="_blank" rel="noopener noreferrer" style={st.btnGold}>View PDF</a>}
                  {r.official_url && <a href={r.official_url} target="_blank" rel="noopener noreferrer" style={st.btn}>Official source</a>}
                  <button onClick={() => removeDoc(r)} style={st.btnDelete} title="Delete from library and AI">Delete</button>
                </div>
              </div>
              {isOld(r) && r.retired_reason && <div style={st.oldWhy}>{r.retired_reason}</div>}
              {r.summary && <div style={st.summary}>{r.summary}</div>}
              <div style={st.meta}>
                {r.issuing_body && <span>{r.issuing_body}</span>}
                {r.applies_to && <span style={st.applies}>Applies to: {r.applies_to}</span>}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

const st = {
  muted: { fontSize: 13, color: 'var(--muted)', padding: 12 },
  empty: { fontSize: 13, color: 'var(--muted)', padding: '24px 12px', textAlign: 'center', background: 'var(--hull)', border: '0.5px solid var(--border)', borderRadius: 10 },
  catHead: { fontSize: 11, color: 'var(--gold2)', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700, marginBottom: 10, paddingBottom: 6, borderBottom: '0.5px solid var(--border)' },
  card: { background: 'var(--hull)', border: '0.5px solid var(--border)', borderRadius: 12, padding: '14px 18px', marginBottom: 10 },
  cardTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 },
  code: { fontSize: 15, fontWeight: 700, color: 'var(--text)' },
  edition: { fontSize: 11, color: 'var(--muted)' },
  fullName: { fontSize: 12, color: 'var(--muted)', marginTop: 2 },
  summary: { fontSize: 13, color: 'var(--text)', lineHeight: 1.55, marginTop: 10 },
  meta: { display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10, fontSize: 11, color: 'var(--muted)' },
  oldTag: { fontSize: 10, padding: '1px 7px', borderRadius: 5, border: '0.5px solid var(--border)', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em' },
  oldWhy: { fontSize: 12, color: 'var(--muted)', fontStyle: 'italic', marginTop: 6 },
  applies: { color: 'var(--gold2)' },
  btn: { fontSize: 12, padding: '5px 11px', borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--ink2)', color: 'var(--muted)', textDecoration: 'none', whiteSpace: 'nowrap' },
  btnDelete: { fontSize: 12, padding: '5px 12px', borderRadius: 7, border: '0.5px solid rgba(224,112,112,.5)', background: 'rgba(224,112,112,.10)', color: '#b4313c', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  btnGold: { fontSize: 12, padding: '5px 11px', borderRadius: 7, border: '0.5px solid var(--gold2)', background: 'rgba(201,147,58,.12)', color: 'var(--gold2)', textDecoration: 'none', whiteSpace: 'nowrap' },
  pendWrap: { background: 'rgba(201,147,58,.06)', border: '0.5px solid var(--gold2)', borderRadius: 12, padding: '14px 18px', marginBottom: 24 },
  pendHd: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, fontSize: 11, color: 'var(--gold2)', textTransform: 'uppercase', letterSpacing: '.07em', fontWeight: 700 },
  pendCount: { fontSize: 11, color: 'var(--muted)', textTransform: 'none', letterSpacing: 0, fontWeight: 400 },
  pendRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderTop: '0.5px solid var(--border)' },
  pendTitle: { fontSize: 13, fontWeight: 500, color: 'var(--text)' },
  pendMeta: { fontSize: 11, color: 'var(--muted)', marginTop: 2 },
  pendView: { fontSize: 12, padding: '5px 10px', borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--ink2)', color: 'var(--muted)', textDecoration: 'none', whiteSpace: 'nowrap' },
  pendApprove: { fontSize: 12, padding: '5px 12px', borderRadius: 7, border: '0.5px solid #137050', background: 'rgba(93,186,142,.12)', color: '#137050', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  pendDismiss: { fontSize: 12, padding: '5px 12px', borderRadius: 7, border: '0.5px solid var(--border)', background: 'none', color: 'var(--muted)', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
}
